import React, { useCallback, useMemo } from 'react';

import {
  LegacyBox,
  TableCell,
  Menu,
  MenuItem,
  IconButton,
  AssignmentIcon,
} from '@gogame/components';
import { useScreenModalV2 } from '@gogame/screens';
import { useParams } from '@gogame/screens';

import {
  BudgetAmount,
  BudgetPercent,
  BudgetDaily,
  Metric,
  Comment,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/Fields';
import BudgetLeft from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/BudgetLeft';
import MetricApplyBelowControl from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/MetricApplyBelowControl';
import MetricCalculated from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/MetricCalculated';
import { BudgetAllocated } from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/ValueConsumers';
import {
  ActivityTypesIndicator,
  TableRowActivityTypes,
  TableCellDefaultBackground,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components';
import {
  GroupActivityType,
  GroupMetricsProvider,
  GroupCMetricsProvider,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import {
  useParamsEntityIds,
  useViewStateByResolveName,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/ViewState';
import {
  ACTIVITY_TYPES_LABELS,
  REGS,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';
import SummaryByDirectorsDialog from 'scenes/MarketingPlanning/components/SummaryByDirectorsDialog';
import SummaryByManagersDialog from 'scenes/MarketingPlanning/components/SummaryByManagersDialog';
import SummaryByOwnersDialog from 'scenes/MarketingPlanning/components/SummaryByOwnersDialog';
import { getMetricTypeById } from 'scenes/MarketingPlanning/utils';

import Actions from './Actions';

const Row = ({
  activityType,
  activityTypeIndex,
  activityTypeLength,
  withBudgetPercent,
  withMetricApplyBelow,
  withAllocatedBudgetLeft,
  withCBudgetEmpty,
}) => {
  const activityTypeLabel = ACTIVITY_TYPES_LABELS[activityType];
  const { metricTypes } = useViewStateByResolveName();
  const showSummaryByOwnersDialog = useScreenModalV2(SummaryByOwnersDialog);
  const showSummaryByManagersDialog = useScreenModalV2(SummaryByManagersDialog);
  const showSummaryByDirectorsDialog = useScreenModalV2(
    SummaryByDirectorsDialog
  );
  const { year, month } = useParams();

  const {
    networkId,
    applicationId,
    channelId,
    platformId,
    affiliateId,
    countryId,
    managerId,
  } = useParamsEntityIds();

  const dialogParams = useMemo(() => {
    let result = { year, month, filters: {} };
    if (networkId) {
      result.filters.networks = [networkId];
    }
    if (applicationId) {
      result.filters.applications = [applicationId];
    }
    if (channelId) {
      result.filters.channels = [channelId];
    }
    if (platformId) {
      result.filters.platforms = [platformId];
    }
    if (affiliateId) {
      result.filters.affiliates = [affiliateId];
    }
    if (countryId) {
      result.filters.countries = [countryId];
    }
    if (managerId) {
      result.filters.managers = [managerId];
    }
    return result;
  }, [
    year,
    month,
    networkId,
    applicationId,
    channelId,
    platformId,
    affiliateId,
    countryId,
    managerId,
  ]);

  const handleSummaryByOwners = useCallback(() => {
    showSummaryByOwnersDialog(dialogParams);
  }, [dialogParams]);

  const handleSummaryByManagers = useCallback(() => {
    showSummaryByManagersDialog(dialogParams);
  }, [dialogParams]);

  const handleSummaryByDirectors = useCallback(() => {
    showSummaryByDirectorsDialog(dialogParams);
  }, [dialogParams]);

  return (
    <TableRowActivityTypes label={activityTypeLabel} type={activityType}>
      <GroupActivityType type={activityType}>
        <TableCellDefaultBackground
          label={`activityType_budgetLeft`}
          borderRight={true}
        >
          <BudgetLeft />
        </TableCellDefaultBackground>
        <TableCellDefaultBackground
          label={`activityType_${activityTypeLabel}`}
          align={'center'}
        >
          {activityTypeLabel}
        </TableCellDefaultBackground>
        <TableCell padding={'none'}>
          <ActivityTypesIndicator type={activityType} />
        </TableCell>
        <TableCell align={'right'} label={'budget-allocated'}>
          {withCBudgetEmpty ? '-' : <BudgetAllocated />}
        </TableCell>
        <TableCell label={'budget-amount'}>
          <BudgetAmount />
        </TableCell>
        {withBudgetPercent ? (
          <TableCell label={'budget-percent'}>
            <BudgetPercent />
          </TableCell>
        ) : null}
        <TableCell label={'budget-daily'}>
          <BudgetDaily />
        </TableCell>
        {metricTypes.map(({ id, kindId }, i) => {
          const typeField = getMetricTypeById(kindId);
          return (
            <React.Fragment key={`metric_group_${id}`}>
              <GroupMetricsProvider>
                <TableCell label={`metric_${i}`}>
                  <LegacyBox gutter={4}>
                    <Metric name={id} width={'108px'} typeField={typeField} />
                    {withMetricApplyBelow && id !== REGS ? (
                      <MetricApplyBelowControl metric={id} />
                    ) : null}
                  </LegacyBox>
                </TableCell>
              </GroupMetricsProvider>
              <GroupCMetricsProvider>
                <TableCell label={`metric_calculated_${i}`}>
                  <MetricCalculated metric={id} typeField={typeField} />
                </TableCell>
              </GroupCMetricsProvider>
            </React.Fragment>
          );
        })}
        <TableCell label={'comment'}>
          <Comment />
        </TableCell>
        <TableCell />
        <TableCell borderLeft={true} label={'actions'}>
          <Actions withAllocatedBudgetLeft={withAllocatedBudgetLeft} />
        </TableCell>
        {activityTypeIndex === 0 ? (
          <TableCellDefaultBackground
            rowSpan={activityTypeLength}
            borderLeft={true}
            label={'view-summary'}
          >
            <Menu
              anchor={
                <IconButton
                  icon={<AssignmentIcon />}
                  tooltip={'View summary'}
                />
              }
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleSummaryByOwners}>
                Summary by owners
              </MenuItem>
              <MenuItem onClick={handleSummaryByManagers}>
                Summary by managers
              </MenuItem>
              <MenuItem onClick={handleSummaryByDirectors}>
                Summary by directors
              </MenuItem>
            </Menu>
          </TableCellDefaultBackground>
        ) : null}
      </GroupActivityType>
    </TableRowActivityTypes>
  );
};

export default Row;
